import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ActionCard } from '~/eds/src/components/Card/subcomponents/ActionCard';
import { UserAction } from '~/eds/src/types/UserAction';
import { slices } from '~/redux/slices';

import { LibraryModelVersion } from '../types';
import { useModelLibraryCart } from './hooks';
import {
  getEntityTypeIconProps,
  getStatusTextProps,
  testIsModelPublished,
} from './utils';

interface Props {
  model: LibraryModelVersion;
  search?: string;
  size?: 'xs' | 'm';
}

export const ModelCard = ({ model, search, size = 'm' }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { addModelToCart, removeModelFromCart } = useModelLibraryCart(intl);

  const category = useSelector(slices.xRayLibrary.selectors.selectCategory);
  const isPublished = testIsModelPublished(model);
  const entityIcon = getEntityTypeIconProps(model, intl);
  const modelsInCart = useSelector(slices.xRayLibrary.selectors.selectCart);
  const isModelInCart = Boolean(
    modelsInCart.find(
      (cartModel) => cartModel.libraryModelId === model.libraryModelId,
    ),
  );
  const isCardInCart = isModelInCart && size === 'xs';
  const modelActions = useMemo(() => {
    const actions: UserAction[] = [];
    if (isPublished) {
      actions.push({
        level: 'secondary' as const,
        mode: 'chip',
        status: 'success' as const,
        text: intl.formatMessage({
          id: 'ModelCard.viewModel',
          defaultMessage: 'View Model',
          description: 'Button text to view model details',
        }),
        onClick: () =>
          dispatch(slices.xRayLibrary.actions.setModelPreview(model)),
      });
    }
    if (!isModelInCart && !isPublished) {
      actions.push({
        icon: 'plus-circle' as const,
        iconPosition: 'left' as const,
        level: 'secondary' as const,
        mode: 'chip',
        status: 'active' as const,
        text: intl.formatMessage({
          id: 'ModelCard.addModel',
          defaultMessage: 'Add Model',
          description: 'Button text to add model to cart',
        }),
        onClick: () => addModelToCart(model),
      });
    }
    if (isModelInCart) {
      if (isCardInCart) {
        actions.push({
          icon: 'trash' as const,
          mode: 'icon',
          text: intl.formatMessage({
            id: 'ModelCard.removeIconText',
            defaultMessage: 'Remove',
            description: 'Button text to remove model from cart',
          }),
          tooltip: intl.formatMessage({
            id: 'ModelCard.removeIconTooltip',
            defaultMessage: 'Remove',
            description: 'Tooltip text for icon to remove model from cart',
          }),
          onClick: () => removeModelFromCart(model),
        });
      } else {
        actions.push({
          icon: 'trash' as const,
          iconPosition: 'left' as const,
          level: 'secondary' as const,
          mode: 'chip',
          status: 'danger',
          text: intl.formatMessage({
            id: 'ModelCard.remove',
            defaultMessage: 'Remove',
            description: 'Button text to remove model from cart',
          }),
          onClick: () => removeModelFromCart(model),
        });
      }
    }
    return actions;
  }, [category, isPublished, isModelInCart, model]);

  const statusText = getStatusTextProps(model, intl);

  return (
    <ActionCard
      actions={modelActions}
      description={model.description}
      descriptionLimit={3}
      icon={entityIcon}
      height="156px" // accomodates 2-line title plus 3-line description
      name={`library-model-card-${model.libraryModelId}`}
      search={search}
      size={isCardInCart ? 'xs' : 'm'}
      statusText={statusText}
      title={model.label}
      titleLimit={2}
      onClick={() => {
        dispatch(slices.xRayLibrary.actions.setModelPreview(model));
        dispatch(slices.xRayLibrary.actions.setCarousel(!isCardInCart));
      }}
    />
  );
};
